/** Types partagés de l'interface admin */

// --- Réponse paginée générique ---
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// --- Sites ---
export interface Site {
  id: number;
  name: string;
  address: string | null;
  is_active: boolean;
}

// --- Employés ---
export type EmployeeActivity = 'boulangerie' | 'traiteur' | 'chambre_froide' | 'cleaning' | 'driving' | 'office';

/** Libellé FR de chaque métier/équipe (partagé par toutes les pages). */
export const ACTIVITY_LABEL: Record<EmployeeActivity, string> = {
  boulangerie: 'Boulangerie',
  traiteur: 'Traiteur',
  chambre_froide: 'Chambre froide',
  cleaning: 'Nettoyage',
  driving: 'Chauffeur',
  office: 'Bureau',
};

export interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  site_id: number;
  site_name: string | null;
  badge_uid: string | null;
  email: string | null;
  department: string | null;
  activity: EmployeeActivity;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmployeeCreate {
  first_name: string;
  last_name: string;
  site_id: number;
  badge_uid?: string;
  email?: string;
  department?: string;
  activity?: EmployeeActivity;
}

export interface EmployeeUpdate {
  first_name?: string;
  last_name?: string;
  site_id?: number;
  email?: string;
  department?: string;
  activity?: EmployeeActivity;
  is_active?: boolean;
}

// --- Pointages ---
export interface TimeLog {
  id: number;
  employee: { id: number; first_name: string; last_name: string; activity: EmployeeActivity };
  reader: { id: number; code: string; name: string };
  type: 'IN' | 'OUT';
  timestamp: string;
  has_photo?: boolean;
}

export interface DailySummaryEmployee {
  employee_id: number;
  employee_name: string;
  first_in: string | null;
  last_out: string | null;
  total_hours: number;
  punches: { type: string; time: string }[];
}

// --- Résumé heures prévues vs réalisées ---
export interface WeeklySummaryDay {
  date: string;
  day_label: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  scheduled_hours: number;
  actual_first_in: string | null;
  actual_last_out: string | null;
  actual_hours: number;
  delta_hours: number;
  is_working_day: boolean;
}

export interface WeeklySummaryResponse {
  employee_id: number;
  employee_name: string;
  date_from: string;
  date_to: string;
  days: WeeklySummaryDay[];
  total_scheduled_hours: number;
  total_actual_hours: number;
  total_delta_hours: number;
}

// --- Bornes ---
export interface Reader {
  id: number;
  code: string;
  name: string;
  site_id: number;
  site_name: string | null;
  location: string | null;
  is_active: boolean;
  last_punch: string | null;
  status: 'online' | 'offline';
  paired: boolean;
  created_at: string;
}

export interface ReaderCreate {
  code: string;
  name: string;
  site_id: number;
  location?: string;
}

// --- Dashboard ---
export interface DashboardStats {
  today: {
    present_count: number;
    absent_count: number;
    total_employees: number;
  };
  readers_status: {
    code: string;
    name: string;
    site_name: string;
    last_punch: string | null;
    status: 'online' | 'offline';
  }[];
}

export interface PresenceData {
  timestamp: string;
  present: {
    id: number;
    name: string;
    department: string | null;
    site_name: string;
    arrived_at: string;
  }[];
  absent_today: {
    id: number;
    name: string;
    department: string | null;
    site_name: string;
    was_scheduled: boolean;
    scheduled_start: string | null;
    scheduled_end: string | null;
    has_justified_absence: boolean;
    absence_reason: string | null;
  }[];
}

// --- Auth ---
export interface User {
  username: string;
}
